import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface PromoCode {
  id: string;
  code: string;
  hits_amount: number;
  max_uses: number | null;
  current_uses: number;
  is_active: boolean;
  is_single_use: boolean;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PromoCodeUsage {
  id: string;
  promo_code_id: string;
  user_id: string;
  used_at: string;
  auto_unlocked_generation_id: string | null;
}

export function usePromoCodes() {
  return useQuery({
    queryKey: ['promo-codes'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('promo_codes')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as PromoCode[];
    },
  });
}

export function useCreatePromoCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (promoCode: {
      code: string;
      hits_amount: number;
      max_uses?: number | null;
      is_single_use?: boolean;
      expires_at?: string | null;
    }) => {
      const { data, error } = await supabase
        .from('promo_codes')
        .insert({
          code: promoCode.code,
          hits_amount: promoCode.hits_amount,
          max_uses: promoCode.max_uses || null,
          is_single_use: promoCode.is_single_use ?? true,
          expires_at: promoCode.expires_at || null,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['promo-codes'] }),
  });
}

export function useUpdatePromoCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<PromoCode> & { id: string }) => {
      const { error } = await supabase
        .from('promo_codes')
        .update(updates)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['promo-codes'] }),
  });
}

export function useDeletePromoCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('promo_codes')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['promo-codes'] }),
  });
}

export function usePromoCodeUsages(promoCodeId?: string) {
  return useQuery({
    queryKey: ['promo-code-usages', promoCodeId],
    queryFn: async () => {
      let query = supabase
        .from('promo_code_usages')
        .select(`
          *,
          telegram_users:user_id(id, username, first_name, last_name)
        `)
        .order('used_at', { ascending: false });
      
      if (promoCodeId) {
        query = query.eq('promo_code_id', promoCodeId);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    enabled: !!promoCodeId,
  });
}

export function usePromoAutoUnlockStats() {
  return useQuery({
    queryKey: ['promo-auto-unlock-stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('promo_code_usages')
        .select(`
          id,
          promo_code_id,
          auto_unlocked_generation_id,
          used_at,
          promo_codes:promo_code_id(code)
        `)
        .not('auto_unlocked_generation_id', 'is', null)
        .order('used_at', { ascending: false });
      if (error) throw error;
      return data;
    },
  });
}
